import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAtom } from 'jotai';
import { X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { env } from '@/constants/env';
import { getImages } from '@/features/estate-media/api/images';
import { SelectedImage } from '@/features/estate-media/components/selected-image';
import { createTempId } from '@/utils/tempId';

import { ImageInAtom, selectedImageAtom, uploadedImagesAtom } from '../atoms/imageFiles';

export interface MediaLibraryModalProps {
  estateId: string;
}

interface MediaImage {
  estateId: string | null;
  id: string;
  name: string | null;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
  creatorId: string | null;
  userId: string | null;
  mediaId?: string | null;
  fileName: string | null;
}

export const MediaLibraryModal = ({ estateId }: MediaLibraryModalProps) => {
  const { t } = useTranslation();

  const [images, setImages] = useState<MediaImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setSelectedImage] = useAtom(selectedImageAtom);
  const [, setUploadedImages] = useAtom(uploadedImagesAtom);
  const [createdImage, setCreatedImage] = useState<ImageInAtom>();

  const handleSelectImage = (image: MediaImage) => {
    if (image.mediaId) {
      setCreatedImage({
        id: createTempId(),
        originalImage: `${env.API_URL}/images/${image.mediaId}`,
        originalImageId: image.mediaId,
        file: null,
        fileType: 'image/jpeg',
      } satisfies ImageInAtom);
    }
  };

  const handleSave = () => {
    if (createdImage) {
      setSelectedImage(createdImage);
      setUploadedImages((prev) => [...prev, createdImage]);
    }
    closeModal();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getImages(estateId);

        setImages(res.images);
      } finally {
        setLoading(false);
      }
    };

    if (estateId) {
      fetchData();
    }
  }, [estateId]);

  return (
    <ModalBase className="flex min-h-[600px] w-full max-w-[900px] flex-col overflow-scroll rounded-sm p-0">
      <div className="flex h-[60px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6">
        <span className="text-sm font-semibold text-white">{t('mediaLibrary')}</span>
        <X className="cursor-pointer stroke-[4px] text-white" onClick={closeModal} />
      </div>
      <div className="flex flex-1 flex-col p-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center bg-background">
            <Spinner className="size-12 [&_*]:stroke-primary-dark" />
          </div>
        ) : images.length ? (
          <>
            <div className="grid w-full grid-cols-6 gap-1 max-md:grid-cols-3 max-sm:grid-cols-2">
              {images.map((file) => (
                <button
                  key={file.id}
                  className="cursor-pointer whitespace-normal break-words px-1"
                  onClick={() => handleSelectImage(file)}
                >
                  <SelectedImage
                    fileType={'fileType' in file ? (file.fileType as string) : ''}
                    fileName={file.fileName as string}
                    mediaId={file?.mediaId as string}
                    onClick={() => handleSelectImage(file)}
                    isSelected={createdImage?.originalImageId === file.mediaId}
                  />
                </button>
              ))}
            </div>
            <div className="mt-auto flex justify-end">
              <Button className="h-[38px]" disabled={!createdImage} onClick={handleSave}>
                {t('save')}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <span>{t('noContent')}</span>
          </div>
        )}
      </div>
    </ModalBase>
  );
};
